const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

function interpolateColor(color1, color2, t) {
  const rgb1 = color1.match(/\d+/g).map(Number);
  const rgb2 = color2.match(/\d+/g).map(Number);
  const r = Math.round(rgb1[0] + (rgb2[0] - rgb1[0]) * t);
  const g = Math.round(rgb1[1] + (rgb2[1] - rgb1[1]) * t);
  const b = Math.round(rgb1[2] + (rgb2[2] - rgb1[2]) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

function withAlpha(color, alpha = 1) {
  const parts = color.match(/\d+/g);
  if (!parts || parts.length < 3) return color;
  const [r, g, b] = parts.slice(0, 3).map(Number);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const TREND_COLOR_SCHEMES = {
  gain_loss: {
    id: 'gain_loss',
    label: 'Red-Green',
    stops: [
      // magenta -> red -> orange -> white -> lime -> green -> teal
      // Wide hue range for perceptual punch while staying race-intuitive.
      { value: -14, color: 'rgb(170, 0, 120)' },     // deep magenta
      { value: -9,  color: 'rgb(255, 25, 25)' },      // vivid red
      { value: -4,  color: 'rgb(255, 140, 60)' },     // warm orange
      { value: -1,  color: 'rgb(255, 225, 210)' },    // soft peach
      { value: 0,   color: 'rgb(248, 248, 248)' },    // white center
      { value: 2,   color: 'rgb(200, 245, 170)' },    // warm lime (yellowish green)
      { value: 6,   color: 'rgb(30, 200, 40)' },      // pure green
      { value: 10,  color: 'rgb(0, 220, 160)' },      // seafoam/minty
      { value: 14,  color: 'rgb(0, 240, 255)' }       // bright cyan
    ]
  },
  legacy_blue_red: {
    id: 'legacy_blue_red',
    label: 'Legacy (Blue-Red)',
    stops: [
      { value: -14, color: 'rgb(0, 20, 80)' },
      { value: -4, color: 'rgb(100, 150, 255)' },
      { value: 0, color: 'rgb(255, 255, 255)' },
      { value: 2, color: 'rgb(255, 150, 150)' },
      { value: 6, color: 'rgb(255, 0, 0)' },
      { value: 10, color: 'rgb(200, 0, 255)' },
      { value: 14, color: 'rgb(0, 255, 255)' }
    ]
  }
};

export const DEFAULT_TREND_COLOR_SCHEME = 'gain_loss';

export function resolveTrendColorSchemeId(schemeId) {
  const normalized = String(schemeId || '').trim().toLowerCase();
  return TREND_COLOR_SCHEMES[normalized] ? normalized : DEFAULT_TREND_COLOR_SCHEME;
}

export function getColorMultiplierFromSensitivity(sensitivity) {
  const safe = clamp(Number(sensitivity) || 0, -100, 100);
  if (safe >= 0) {
    return 1.0 + (safe / 100) * 1.0;
  }
  return 1.0 + (safe / 100) * 0.5;
}

export function getTrendScaleValue(deltaSeconds, trendSeconds, multiplier = 1, invertSign = false) {
  if (!Number.isFinite(deltaSeconds) || !Number.isFinite(trendSeconds) || !Number.isFinite(multiplier)) {
    return null;
  }

  const windowSec = Math.max(1, Math.abs(trendSeconds));
  const normalized = deltaSeconds / (windowSec * 0.5);
  // Global mapping rule: lower values -> red, higher values -> green.
  // XC pace deltas are the exception: negative means better, so callers set invertSign=true.
  const signed = invertSign ? -normalized : normalized;
  const varioLikeKt = signed * 6;
  const scaled = varioLikeKt * multiplier;
  return clamp(scaled, -14, 14);
}

export function getTrendColorFromScale(scaleValue, schemeId = DEFAULT_TREND_COLOR_SCHEME) {
  if (!Number.isFinite(scaleValue)) {
    return null;
  }

  const resolvedScheme = TREND_COLOR_SCHEMES[resolveTrendColorSchemeId(schemeId)];
  const stops = resolvedScheme.stops;

  if (scaleValue <= stops[0].value) {
    return stops[0].color;
  }

  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i];
    const b = stops[i + 1];
    if (scaleValue <= b.value) {
      const range = b.value - a.value;
      const t = range === 0 ? 0 : (scaleValue - a.value) / range;
      return interpolateColor(a.color, b.color, t);
    }
  }

  return stops[stops.length - 1].color;
}

export function getTrendColorFromDelta(deltaSeconds, trendSeconds, options = {}) {
  const {
    multiplier = 1,
    schemeId = DEFAULT_TREND_COLOR_SCHEME,
    invertSign = false,
    nullColor = null
  } = options;

  const scaleValue = getTrendScaleValue(deltaSeconds, trendSeconds, multiplier, invertSign);
  if (!Number.isFinite(scaleValue)) {
    return nullColor;
  }

  return getTrendColorFromScale(scaleValue, schemeId);
}

export function getTrendSliderGradient(options = {}) {
  const {
    schemeId = DEFAULT_TREND_COLOR_SCHEME,
    alpha = 0.6,
    direction = 'to right'
  } = options;

  const resolvedScheme = TREND_COLOR_SCHEMES[resolveTrendColorSchemeId(schemeId)];
  const stops = resolvedScheme.stops;
  const min = stops[0].value;
  const max = stops[stops.length - 1].value;
  const range = Math.max(1, max - min);

  const gradientStops = stops
    .map((stop) => {
      const pct = ((stop.value - min) / range) * 100;
      return `${withAlpha(stop.color, alpha)} ${pct.toFixed(2)}%`;
    })
    .join(', ');

  return `linear-gradient(${direction}, ${gradientStops})`;
}
